import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    IconButton,
    Tooltip,
    Modal,
    TextField,
    MenuItem,
    Button,
    Grid,
    CircularProgress,
    Chip
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Security as SecurityIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import api from '../../services/api';
import Preloader from '../../components/Preloader';
import { formatarDataHora, formatarData, converterDataParaISO } from '../../utils/formatadores';

export default function ListagemAuditoria() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    // Filtros
    const [filtros, setFiltros] = useState({
        entidade: '',
        acao: '',
        usuario: '',
        dataInicio: '',
        dataFim: ''
    });
    const [opcoesFiltros, setOpcoesFiltros] = useState({ entidades: [], acoes: [] });

    // Modal
    const [modalOpen, setModalOpen] = useState(false);
    const [logSelecionado, setLogSelecionado] = useState(null);

    useEffect(() => {
        carregarFiltros();
    }, []);

    useEffect(() => {
        carregarLogs();
    }, [page, rowsPerPage]);

    const carregarFiltros = async () => {
        try {
            const response = await api.get('/auditoria/filtros');
            if (response.data.status === 'sucesso') {
                setOpcoesFiltros(response.data.dados);
            }
        } catch (error) {
            console.error('Erro ao carregar filtros:', error);
        }
    };

    const carregarLogs = async () => {
        setLoading(true);
        try {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                ...filtros
            };

            // Converter datas BR para ISO antes de enviar
            if (params.dataInicio) params.dataInicio = converterDataParaISO(params.dataInicio);
            if (params.dataFim) params.dataFim = converterDataParaISO(params.dataFim);

            // Limpar filtros vazios
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null) {
                    delete params[key];
                }
            });

            const response = await api.get('/auditoria', { params });
            if (response.data.status === 'sucesso') {
                setLogs(response.data.dados);
                setTotal(response.data.meta.total);
            }
        } catch (error) {
            console.error('Erro ao buscar logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        if (name === 'dataInicio' || name === 'dataFim') {
            setFiltros({ ...filtros, [name]: formatarData(value) });
        } else {
            setFiltros({ ...filtros, [name]: value });
        }
    };

    const handleSearch = () => {
        setPage(0);
        carregarLogs();
    };

    const visualizarLog = (log) => {
        setLogSelecionado(log);
        setModalOpen(true);
    };

    const getAcaoColor = (acao) => {
        switch (acao) {
            case 'CRIAR': return 'success';
            case 'ATUALIZAR': return 'info';
            case 'EXCLUIR': return 'error';
            case 'LOGIN': return 'warning';
            default: return 'default';
        }
    };

    return (
        <Box sx={{ p: 3, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <SecurityIcon sx={{ fontSize: '2rem', color: '#20ac9c' }} />
                <Typography variant="h5" component="h1">
                    Logs de Auditoria
                </Typography>
            </Box>

            {/* Filtros */}
            <Paper sx={{
                p: 2,
                mb: 3,
                boxShadow: (theme) => theme.palette.mode === 'dark'
                    ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                    : '0 2px 8px rgba(0, 0, 0, 0.06)',
                borderRadius: 2,
                border: 'none'
            }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                        <TextField
                            select
                            fullWidth
                            label="Entidade"
                            name="entidade"
                            value={filtros.entidade}
                            onChange={handleFilterChange}
                            size="small"
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {opcoesFiltros.entidades.map(ent => (
                                <MenuItem key={ent} value={ent}>{ent}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' } }}>
                        <TextField
                            select
                            fullWidth
                            label="Ação"
                            name="acao"
                            value={filtros.acao}
                            onChange={handleFilterChange}
                            size="small"
                        >
                            <MenuItem value="">Todas</MenuItem>
                            {opcoesFiltros.acoes.map(ac => (
                                <MenuItem key={ac} value={ac}>{ac}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '2 1 300px' } }}>
                        <TextField
                            fullWidth
                            label="Usuário (Nome ou Email)"
                            name="usuario"
                            value={filtros.usuario}
                            onChange={handleFilterChange}
                            size="small"
                        />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                        <TextField
                            fullWidth
                            label="Data Início"
                            name="dataInicio"
                            value={filtros.dataInicio}
                            onChange={handleFilterChange}
                            size="small"
                            placeholder="DD/MM/AAAA"
                            inputProps={{ maxLength: 10 }}
                        />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' } }}>
                        <TextField
                            fullWidth
                            label="Data Fim"
                            name="dataFim"
                            value={filtros.dataFim}
                            onChange={handleFilterChange}
                            size="small"
                            placeholder="DD/MM/AAAA"
                            inputProps={{ maxLength: 10 }}
                        />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' }, minWidth: 120 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            sx={{ bgcolor: '#20ac9c', '&:hover': { bgcolor: '#167a6f' }, height: '40px' }}
                        >
                            Filtrar
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {/* Tabela */}
            <TableContainer
                component={Paper}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'auto',
                    height: {
                        xs: 'calc(90vh - 140px)',
                        sm: 'calc(90vh - 120px)',
                        md: 'calc(90vh - 120px)',
                        lg: 'calc(90vh - 120px)'
                    },
                    maxHeight: {
                        xs: 'calc(90vh - 140px)',
                        sm: 'calc(90vh - 120px)',
                        md: 'calc(90vh - 120px)',
                        lg: 'calc(90vh - 120px)'
                    },
                    width: '100%',
                    minWidth: { xs: '100%', sm: '600px' },
                    maxWidth: '100%',
                    boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                        : '0 2px 8px rgba(0, 0, 0, 0.06)',
                    borderRadius: 2,
                    border: 'none'
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data/Hora</TableCell>
                            <TableCell>Usuário</TableCell>
                            <TableCell>Entidade</TableCell>
                            <TableCell>Ação</TableCell>
                            <TableCell>Registro (ID)</TableCell>
                            <TableCell align="center">Detalhes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <CircularProgress size={30} />
                                </TableCell>
                            </TableRow>
                        ) : logs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    Nenhhum registro encontrado.
                                </TableCell>
                            </TableRow>
                        ) : (
                            logs.map((log) => (
                                <TableRow key={log._id || log.id_auditoria} hover>
                                    <TableCell>
                                        {formatarDataHora(log.dt_evento)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {log.usuario?.st_nome || 'Sistema'}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {log.usuario?.st_email || '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{log.st_entidade}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={log.st_acao}
                                            size="small"
                                            color={getAcaoColor(log.st_acao)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{log.id_registro_afetado || '-'}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Ver Dados">
                                            <IconButton size="small" onClick={() => visualizarLog(log)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                labelRowsPerPage="Linhas por página"
            />

            {/* Modal de Detalhes */}
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', md: '600px' },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    maxHeight: '90vh',
                    overflowY: 'auto'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                            Detalhes da Auditoria
                        </Typography>
                        <IconButton onClick={() => setModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {logSelecionado && (
                        <Box id="modal-modal-description">
                            <Typography variant="subtitle2" color="textSecondary">Metadados</Typography>
                            <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1, mb: 2 }}>
                                <Typography variant="caption" display="block">IP: {logSelecionado.st_ip}</Typography>
                                <Typography variant="caption" display="block">User Agent: {logSelecionado.st_user_agent}</Typography>
                            </Box>

                            {logSelecionado.tx_dados_originais && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary">Dados Anteriores</Typography>
                                    <Paper sx={{ p: 1, bgcolor: '#f5f5f5', overflowX: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                                            {JSON.stringify(logSelecionado.tx_dados_originais, null, 2)}
                                        </pre>
                                    </Paper>
                                </Box>
                            )}

                            {logSelecionado.tx_dados_novos && (
                                <Box>
                                    <Typography variant="subtitle2" color="secondary">Dados Novos</Typography>
                                    <Paper sx={{ p: 1, bgcolor: '#f0f8ff', overflowX: 'auto' }}>
                                        <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                                            {JSON.stringify(logSelecionado.tx_dados_novos, null, 2)}
                                        </pre>
                                    </Paper>
                                </Box>
                            )}

                            {!logSelecionado.tx_dados_originais && !logSelecionado.tx_dados_novos && (
                                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                                    Nenhum dado detalhado registrado para esta ação.
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </Modal>
        </Box>
    );
}
